import React, { useId, useState } from 'react';
import { motion } from 'framer-motion';
import { ContentSection, LinkRow, PageFrame, PageHero } from '../components/PageBlocks';
import { getPageConfig } from '../data/page-config.mjs';
import { companyFaqs, faqs } from '../data/faqs.mjs';

interface Faq {
    q: string;
    a: string;
}

function FAQItem({ faq, index }: { faq: Faq; index: number }) {
    const [open, setOpen] = useState(false);
    const panelId = useId();
    const questionId = `${panelId}-question`;

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            className="faq-item faq-item--numbered"
        >
            <h2>
                <button
                    id={questionId}
                    type="button"
                    onClick={() => setOpen((value) => !value)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    className="faq-trigger"
                >
                    <span className="faq-numeral" aria-hidden="true">
                        {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="faq-title">{faq.q}</span>
                    <motion.span
                        animate={{ rotate: open ? 45 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="faq-icon"
                    >
                        +
                    </motion.span>
                </button>
            </h2>

            <motion.div
                id={panelId}
                role="region"
                aria-labelledby={questionId}
                initial={false}
                animate={{
                    height: open ? 'auto' : 0,
                    opacity: open ? 1 : 0,
                }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ overflow: 'hidden' }}
            >
                <p className="text-body" style={{ paddingBottom: 24 }}>
                    {faq.a}
                </p>
            </motion.div>
        </motion.div>
    );
}

const page = getPageConfig('/faq');

const FAQPage: React.FC = () => {
    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="minimal"
                numeral="VII"
                eyebrow={page.routePurpose}
                title={page.h1}
                intro="Direct answers about what AvenBox is, what exists today, what is still in development, and what the reservation means. The same real / prototype / roadmap discipline that governs the product governs these answers."
                primaryCta={{ label: 'Join Waitlist', to: '/contact' }}
                secondaryCta={{ label: 'Contact', to: '/contact', variant: 'secondary' }}
                proofStrip={[
                    'Real now: local AI backend, orchestration core, privacy layer.',
                    'Prototype now: voice flows, PC interaction, full UX.',
                    'Directional: current pricing and Q1 2027 target.',
                    'Roadmap: finished hardware, platform maturity, polished app.',
                ]}
            />

            <ContentSection
                sectionId="product-faq"
                eyebrow="Questions"
                title="Product, stage, and reservation — stated directly"
                intro="These cover the most common serious questions. For anything not answered here, reach out directly."
                borderTop={false}
            >
                <div className="faq-list">
                    {faqs.map((faq: Faq, index: number) => (
                        <FAQItem key={faq.q} faq={faq} index={index} />
                    ))}
                </div>
            </ContentSection>

            <ContentSection
                sectionId="company-faq"
                eyebrow="About the Company"
                title="Who is building this and why"
            >
                <div className="faq-list">
                    {companyFaqs.map((faq: Faq, index: number) => (
                        <FAQItem key={faq.q} faq={faq} index={index} />
                    ))}
                </div>
            </ContentSection>

            <ContentSection sectionId="continue" eyebrow="Continue" title="Go deeper on any of these topics">
                <LinkRow
                    links={[
                        { label: 'Technology', to: '/technology' },
                        { label: 'Security', to: '/security' },
                        { label: 'Pricing', to: '/pricing' },
                        { label: 'Contact', to: '/contact' },
                    ]}
                />
            </ContentSection>
        </PageFrame>
    );
};

export default FAQPage;
